import type { Cell, Workbook } from 'exceljs';
import type { FormConfig, PageList, PageQuestion } from '../../config/types';
import {
  getListPageExcelSchema,
  validateListPageEditorRows,
} from '../list-page-excel/editor-model';
import { mapPsfaCdrClipboard } from '../list-page-excel/psfa-cdr-clipboard';
import { cdrAnchors, cdrFields, cdrTableEnds, cdrTables } from './layout';

export type CdrSection = {
  id: string;
  title: string;
  group: string;
  page: PageList | PageQuestion;
  declaration?: PageQuestion;
  rows: string[][];
  values: Record<string, unknown>;
  notes: string[];
  sourceErrors?: string[];
};

const normalise = (text: string) => text.trim().replace(/\s+/g, ' ').toLowerCase();
const readCell = (cell: Cell): string => {
  const value = cell.value;
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    if ('richText' in value) return value.richText.map(({ text }) => text).join('');
    if ('hyperlink' in value) return value.text;
    throw new Error(
      `${cell.worksheet.name}!${cell.address}: replace formulas, dates or Excel errors with values before importing.`,
    );
  }
  return String(value);
};

export const parseCdrWorkbook = (
  workbook: Workbook,
  config: FormConfig,
): CdrSection[] | undefined => {
  const sheet = workbook.getWorksheet('Proforma');
  const organisation = workbook.getWorksheet('Organisation details');
  if (!sheet && !organisation) return undefined;
  if (!sheet || !organisation || config.id !== 'psfa')
    throw new Error('Use a 2025-26 or 2026-27 CDR workbook with its original worksheets.');
  const templateYear = /\b26\/27 CDR Guidance\b/i.test(sheet.getCell('D6').text)
    ? '2026-27'
    : '2025-26';
  const boundaries = Object.entries(cdrTableEnds).map(([row, [column, heading]]) => {
    const matches: number[] = [];
    sheet.eachRow((candidate, index) => {
      const cell = candidate.getCell(column);
      if (cell.value !== null && normalise(cell.text) === normalise(heading)) matches.push(index);
    });
    if (matches.length !== 1)
      throw new Error(
        `The CDR layout has changed: expected one "${heading}" boundary in column ${column}.`,
      );
    return { original: Number(row), actual: matches[0] };
  });
  let offset = 0;
  for (const boundary of boundaries) {
    if (boundary.actual < boundary.original + offset)
      throw new Error(
        'The CDR layout has changed: table boundaries are out of order or rows were removed.',
      );
    offset = boundary.actual - boundary.original;
  }
  const proformaRow = (row: number) => {
    const preceding = boundaries.filter((boundary) => boundary.original <= row).pop();
    return row + (preceding ? preceding.actual - preceding.original : 0);
  };
  const proformaCell = (address: string) => {
    const [, column, row] = /^([A-Z]+)(\d+)$/.exec(address)!;
    return sheet.getCell(`${column}${proformaRow(Number(row))}`);
  };
  const separateYear = normalise(organisation.getCell('B5').text) === 'reporting year';
  const organisationCell = (address: string) => {
    const [, column, row] = /^([A-Z]+)(\d+)$/.exec(address)!;
    return organisation.getCell(
      `${column}${Number(row) + (separateYear && Number(row) >= 5 ? 1 : 0)}`,
    );
  };
  for (const [address, heading] of Object.entries(cdrAnchors)) {
    if (normalise(proformaCell(address).text) !== normalise(heading)) {
      throw new Error(
        `The CDR layout has changed at Proforma!${address}. Use the original 2025-26 or 2026-27 CDR template.`,
      );
    }
  }
  for (const [address, heading] of Object.entries({
    B2: 'Department:',
    B3: 'Name of public body:',
    B5: 'Reporting quarter',
    B8: 'Name',
    B9: 'Email address',
    B12: 'Name',
    B13: 'Email address',
    B16: 'Name',
    B17: 'Email address',
  })) {
    if (normalise(organisationCell(address).text) !== normalise(heading))
      throw new Error(`The organisation details layout has changed at ${address}.`);
  }
  const sections: CdrSection[] = [];
  for (const group of config.sections)
    for (const subsection of group.subsections) {
      for (const page of subsection.pages) {
        if (page.pageType === 'information' || (!cdrTables[page.name] && !cdrFields[page.name]))
          continue;
        const result: CdrSection = {
          id: subsection.slug,
          title: subsection.title,
          group: group.title,
          page,
          rows: [],
          values: {},
          notes: [],
        };
        result.declaration = subsection.pages.find(
          (candidate): candidate is PageQuestion =>
            candidate.pageType === 'question' &&
            candidate.questions?.length === 1 &&
            candidate.questions[0].name.endsWith('_declare'),
        );
        const table = cdrTables[page.name];
        if (table) {
          const [header, end, count] = table;
          const firstRow = proformaRow(header);
          const rows = Array.from({ length: proformaRow(end) - firstRow }, (_, i) =>
            Array.from({ length: count }, (_, j) =>
              readCell(sheet.getCell(firstRow + i, 5 + j * 2)),
            ),
          );
          const mapped = mapPsfaCdrClipboard(
            rows,
            page.name,
            getListPageExcelSchema(page.questions || []).columns,
          );
          if (mapped.error) throw new Error(`${subsection.title}: ${mapped.error}`);
          result.rows = mapped.rows.filter((row) => row.some((value) => value.trim() !== ''));
          if (mapped.message) result.notes.push(mapped.message);
        } else {
          const sourceCell = page.name === 'department-select' ? organisationCell : proformaCell;
          result.values = Object.fromEntries(
            Object.entries(cdrFields[page.name]).map(([field, address]) => [
              field,
              readCell(sourceCell(address)),
            ]),
          );
          if (page.name === 'detected-bribery') {
            const entries = Array.from(
              { length: proformaRow(115) - proformaRow(112) },
              (_, index) => proformaRow(112) + index,
            )
              .map((row) =>
                ['E', 'G', 'I', 'K'].map((column) => readCell(sheet.getCell(`${column}${row}`))),
              )
              .filter((row) => row.some((value) => value.trim()));
            if (entries.length > 0) {
              const validation = validateListPageEditorRows(
                {
                  title: page.title,
                  pageName: page.name,
                  questions: page.questions || [],
                  answers: {},
                  tenant: 'psfa',
                },
                entries,
              );
              result.sourceErrors = validation.errors.map(
                (error) =>
                  `Workbook bribery row ${error.worksheetRow}: ${error.fieldTitle}: ${error.message}`,
              );
              for (const [field, index] of [
                ['detected_bribery_instances', 1],
                ['detected_bribery', 2],
              ] as const) {
                const values = entries.map((row) => row[index].replace(/[£,\s]/g, ''));
                result.values[field] = values.every((value) => /^\d+(\.\d+)?$/.test(value))
                  ? String(Number(values.reduce((sum, value) => sum + Number(value), 0).toFixed(2)))
                  : values.filter(Boolean).join(' + ');
              }
              const covid = [...new Set(entries.map((row) => normalise(row[0])))];
              result.values.detected_bribery_c19 = covid.length === 1 ? entries[0][0] : '';
              result.values.detected_bribery_comments = entries
                .map((row) =>
                  covid.length > 1 || validation.errors.length > 0
                    ? `COVID-19: ${row[0] || 'not supplied'}; instances: ${row[1]}; value: ${row[2]}. ${row[3]}`
                    : row[3],
                )
                .filter(Boolean)
                .join('\n');
              if (entries.length > 1)
                result.notes.push(
                  'The workbook bribery rows are combined into the form’s single response. Different COVID-19 answers are preserved in Comments and need review.',
                );
            }
          }
          if (page.name === 'department-select') {
            const reportingYear = separateYear ? readCell(organisation.getCell('D5')).trim() : '';
            if (separateYear && !/^(?:20)?\d{2}[-/](?:20)?\d{2}$/.test(reportingYear))
              throw new Error('Organisation details!D5: enter a reporting year such as 2026-27.');
            const period = [
              result.values['report-period'],
              separateYear ? reportingYear.replace(/^(\d{2})([-/])/, '20$1$2') : '',
            ]
              .filter(Boolean)
              .join(' ');
            const quarter =
              period.match(/(?:quarter|q)\s*([1-4])/i) || period.match(/^\s*([1-4])\s*$/);
            const year = period.match(/\b(20\d{2})[-/](?:20)?(\d{2})\b/);
            result.values['report-period'] = quarter
              ? {
                  quarter: `Quarter ${quarter[1]} (${['April-June', 'July-September', 'October-December', 'January-March'][Number(quarter[1]) - 1]})`,
                  year: year ? `${year[1]}-${year[2]}` : templateYear,
                }
              : period;
            if (readCell(organisation.getCell('D4')).trim())
              result.notes.push(
                `Directorate / business unit has no matching form field: ${readCell(organisation.getCell('D4'))}`,
              );
            if (readCell(sheet.getCell('K5')).trim())
              result.notes.push(
                `Overall comments have no matching form field: ${readCell(sheet.getCell('K5'))}`,
              );
          }
        }
        sections.push(result);
      }
    }
  return sections;
};

export const readCdrWorkbook = async (file: File, config: FormConfig) => {
  if (!file.name.toLowerCase().endsWith('.xlsx')) throw new Error('Select an XLSX workbook.');
  if (file.size > 10 * 1024 * 1024) throw new Error('Select a workbook smaller than 10 MB.');
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  return parseCdrWorkbook(workbook, config);
};
